import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { usePermissions } from '../../context/PermissionsContext';
import { PERMISSIONS } from '../../constants/permissions';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Stack,
  CircularProgress,
  Alert,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Avatar,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  Search,
  Refresh,
  OpenInNew,
  NavigateBefore,
  NavigateNext,
  Build,
  Bolt,
  Delete,
  Add,
  Edit,
  MoreVert,
  Share,
  ManageAccounts,
  LockOutlined,
} from '@mui/icons-material';
import { LayoutGrid, List as ListIconLucide } from 'lucide-react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import composioService from '../../services/composioService';
import customToolsService from '../../services/customToolsService';
import resourceSharingService from '../../services/resourceSharingService';
import rbacService from '../../services/rbacService';
import { describeApiError } from '../../utils/apiError';
import ComposioConnectionDialog from './ComposioConnectionDialog';
import CustomToolConfigDialog from './CustomToolConfigDialog';
import CreateCustomToolDialog from './CreateCustomToolDialog';
import CustomToolDeleteDialog from './CustomToolDeleteDialog';
import PageHeader from '../shared/PageHeader';
import PageLoader from '../shared/PageLoader';
import ResourceShareDialog from '../shared/ResourceShareDialog';
import ManageSharingDialog from '../shared/ManageSharingDialog';
import styles from './ToolsConfigure.module.scss';

const ToolsConfigure = () => {
  const { isDarkMode } = useTheme();
  const { has, ready: permissionsReady } = usePermissions();
  const canShareResources = permissionsReady && has(PERMISSIONS.RESOURCE_SHARING);
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialise search from `?search=` so navigations from AgentCreate's
  // "Authenticate <toolkit>" link land on this page with the toolkit pre-filtered.
  const initialSearch = searchParams.get('search') || '';
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [apps, setApps] = useState([]);
  const [connectedApps, setConnectedApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Custom Tools state
  const [customTools, setCustomTools] = useState([]);
  const [configuredCustomTools, setConfiguredCustomTools] = useState([]);
  const [selectedCustomTool, setSelectedCustomTool] = useState(null);
  const [customToolDialogOpen, setCustomToolDialogOpen] = useState(false);

  const [createToolOpen, setCreateToolOpen] = useState(false);
  const [editCustomToolConfig, setEditCustomToolConfig] = useState(null);
  const [editToolDefinition, setEditToolDefinition] = useState(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toolToDelete, setToolToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState('config'); // 'config' or 'definition'
  const [isDeleting, setIsDeleting] = useState(false);

  // Menu state for tools
  const [toolMenuAnchorEl, setToolMenuAnchorEl] = useState(null);
  const [activeToolForMenu, setActiveToolForMenu] = useState(null);
  const [toolToShare, setToolToShare] = useState(null);
  const [toolToManage, setToolToManage] = useState(null);
  const [integrationToShare, setIntegrationToShare] = useState(null);
  const [integrationToManage, setIntegrationToManage] = useState(null);
  const [sharedToolPreview, setSharedToolPreview] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalApps, setTotalApps] = useState(0);
  const [totalAllApps, setTotalAllApps] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  // View mode state with localStorage persistence
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('toolsConfigureViewMode') || 'list';
  });

  // Apps filter: 'all' | 'connected'
  // When arriving with a `search` param (deep-link from AgentCreate to authenticate
  // a specific toolkit), force the filter to 'all' so the target app is reachable
  // — the user may be filtered to "connected" from a previous visit.
  const [appsFilter, setAppsFilter] = useState(() => {
    if (initialSearch) return 'all';
    return localStorage.getItem('toolsConfigureAppsFilter') || 'all';
  });

  // If the URL `search` param changes after mount, sync it into the search box and
  // clear it so refreshes don't keep re-applying the filter. Also persist the
  // "All" filter to localStorage so a refresh of this tab keeps the target app visible.
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchQuery(urlSearch);
      setAppsFilter('all');
      localStorage.setItem('toolsConfigureAppsFilter', 'all');
      const next = new URLSearchParams(searchParams);
      next.delete('search');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleAppsFilterChange = (newFilter) => {
    if (newFilter !== 'all' && newFilter !== 'connected') return;
    setAppsFilter(newFilter);
    localStorage.setItem('toolsConfigureAppsFilter', newFilter);
    setPage(1);
  };

  // Handle view mode change with localStorage persistence (same pattern as Agents page)
  const handleViewModeChange = (newView) => {
    if (newView === 'card' || newView === 'list') {
      setViewMode(newView);
      localStorage.setItem('toolsConfigureViewMode', newView);
    }
  };


  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#e36250',
      },
      secondary: {
        main: '#8b5cf6',
      },
    },
  });

  // Debounce search query
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchQuery]);


  const fetchAppsData = useCallback(async () => {
    console.log('fetchAppsData called - this might be the issue');
    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        page_size: pageSize,
        ...(debouncedSearchQuery && { search: debouncedSearchQuery })
      };

      const [appsResponse, connectedResponse, customToolsResponse, userCustomToolsResponse, configuredToolsResponse] = await Promise.all([
        composioService.getAvailableApps(params),
        composioService.getConnectedApps(),
        customToolsService.getAvailableTools().catch(err => {
          console.error('Error fetching custom tools:', err);
          return { tools: [], total: 0 };
        }),
        customToolsService.getCustomTools().catch(err => {
          console.error('Error fetching user custom tools:', err);
          return [];
        }),
        customToolsService.getConfiguredTools().catch(err => {
          console.error('Error fetching configured custom tools:', err);
          return { configs: [], total: 0 };
        })
      ]);

      setApps(appsResponse.apps || []);
      // Updated to handle new response format with 'connections' array
      setConnectedApps(connectedResponse.connections || connectedResponse.connected_apps || []);
      setTotalApps(appsResponse.total || 0);
      setTotalAllApps(appsResponse.total_all_apps || 0);
      setTotalPages(appsResponse.total_pages || 0);
      setHasNext(appsResponse.has_next || false);
      setHasPrevious(appsResponse.has_previous || false);

      // Set custom tools data - merging system templates and user defined tools
      const systemTools = customToolsResponse.tools || [];
      const userTools = Array.isArray(userCustomToolsResponse) ? userCustomToolsResponse : (userCustomToolsResponse.tools || []);
      const sharedTools = !Array.isArray(userCustomToolsResponse) && Array.isArray(userCustomToolsResponse.shared)
        ? userCustomToolsResponse.shared.map((item) => ({
            ...(item.resource || {}),
            id: item.resource_id ?? item.resource?.id,
            __shared: true,
            __sharePermission: item.permission || 'read',
            __shareOwner: item.owner,
          }))
        : [];

      // MCP SVG Icon
      const mcpIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24" >
          <path d="m19.97,11.84c.66-.66,1.02-1.53,1.02-2.46s-.36-1.8-1.02-2.46l-.04-.04c-.66-.66-1.53-1.02-2.46-1.02-.17,0-.34.03-.51.05.02-.17.05-.33.05-.51,0-.93-.36-1.8-1.02-2.46-.66-.66-1.53-1.02-2.46-1.02s-1.8.36-2.46,1.02l-7.87,7.87c-.27.27-.27.71,0,.98s.71.27.98,0l7.87-7.87c.39-.39.92-.61,1.47-.61s1.08.22,1.47.61c.39.39.61.92.61,1.48s-.22,1.08-.61,1.48l-5.86,5.86-.08.08c-.27.27-.27.71,0,.98.14.14.31.2.49.2s.36-.07.49-.2l5.94-5.94c.39-.39.92-.61,1.48-.61s1.08.22,1.47.61l.04.04c.39.39.61.92.61,1.47s-.22,1.08-.61,1.48l-7.11,7.11c-.63.63-.63,1.66,0,2.29l1.46,1.46c.14.14.31.2.49.2s.36-.07.49-.2c.27-.27.27-.71,0-.98l-1.46-1.46c-.09-.09-.09-.24,0-.33l7.11-7.11Z" /><path d="m17.96,9.83c.27-.27.27-.71,0-.98-.27-.27-.71-.27-.98,0l-5.82,5.82c-.81.81-2.14.81-2.95,0-.81-.81-.81-2.14,0-2.95l5.82-5.82c.27-.27.27-.71,0-.98-.27-.27-.71-.27-.98,0l-5.82,5.82c-1.36,1.36-1.36,3.56,0,4.92.68.68,1.57,1.02,2.46,1.02s1.78-.34,2.46-1.02l5.82-5.82Z" />
        </svg>
      );

      // HTTP/REST SVG Icon
      const httpIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
        </svg>
      );

      // Mark user tools so we can distinguish them if needed (e.g. to allow editing definition)
      const markedUserTools = userTools.map(t => ({
        ...t,
        isUserDefined: true,
        category: 'User Defined',
        icon: t.api_type === 'MCP' ? mcpIcon : httpIcon
      }));

      const markedSharedTools = sharedTools.map(t => ({
        ...t,
        isUserDefined: true,
        category: 'Shared',
        icon: t.api_type === 'MCP' ? mcpIcon : httpIcon,
      }));

      setCustomTools([...markedUserTools, ...markedSharedTools, ...systemTools]);
      setConfiguredCustomTools(configuredToolsResponse.configs || []);
    } catch (err) {
      console.error('Error fetching apps:', err);
      setError('Failed to load apps. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearchQuery]);

  // Fetch apps and connected apps on mount and when filters change
  useEffect(() => {
    console.log('useEffect calling fetchAppsData - dependencies changed');
    fetchAppsData();
  }, [fetchAppsData]);

  // Handle OAuth callback parameters (e.g. from popup callback redirect or direct landing)
  useEffect(() => {
    const oauthStatus = searchParams.get('oauth_status');
    const toolId = searchParams.get('tool_id');
    const oauthError = searchParams.get('error') || searchParams.get('error_description');

    if (oauthStatus) {
      if (window.opener) {
        // Running inside the OAuth popup window: notify the opener and close this popup immediately
        try {
          window.opener.postMessage({
            type: 'CUSTOM_TOOL_OAUTH_STATUS',
            status: oauthStatus,
            tool_id: toolId,
            error: oauthError,
          }, window.location.origin);
        } catch (e) {
          console.warn('Failed to notify window.opener:', e);
        }
        window.close();
      } else {
        // Fallback when not inside a popup (e.g. opened in primary window)
        if (oauthStatus === 'connected') {
          fetchAppsData();
          setSnackbar({
            open: true,
            message: 'Custom tool OAuth connected successfully!',
            severity: 'success',
          });
        } else if (oauthStatus === 'error') {
          setSnackbar({
            open: true,
            message: oauthError ? `OAuth failed: ${oauthError}` : 'OAuth authorization failed',
            severity: 'error',
          });
        }
        // Clean query parameters from URL
        const next = new URLSearchParams(searchParams);
        next.delete('oauth_status');
        next.delete('tool_id');
        next.delete('error');
        next.delete('error_description');
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, fetchAppsData, setSearchParams]);


  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAppsData();
    setRefreshing(false);
    setSnackbar({
      open: true,
      message: 'Apps refreshed successfully',
      severity: 'success'
    });
  };

  // Connection status checking function - available for future use
  // const handleCheckConnectionStatus = async (appId) => {
  //   try {
  //     const status = await composioService.getConnectionStatus(appId);
  //     console.log('Connection status:', status);
  //     
  //     // Refresh the apps data to get updated status
  //     await fetchAppsData();
  //     
  //     setSnackbar({
  //       open: true,
  //       message: `Connection status updated for ${status.app_name || 'app'}`,
  //       severity: 'info'
  //     });
  //   } catch (err) {
  //     console.error('Error checking connection status:', err);
  //     setSnackbar({
  //       open: true,
  //       message: 'Failed to check connection status',
  //       severity: 'error'
  //     });
  //   }
  // };

  const handleAppClick = (app) => {
    console.log('handleAppClick called for app:', app.name);
    setSelectedApp(app);
    setDialogOpen(true);
  };

  const handleConnect = async (appId, connectData = {}) => {
    try {
      const result = await composioService.connectApp(appId, connectData);

      // Don't refresh APIs here - only after OAuth completion
      // The refresh will happen in the dialog when OAuth is successful

      return result;
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to initiate connection to ${selectedApp?.name}`,
        severity: 'error'
      });
      throw err;
    }
  };

  const handleDisconnect = async (appId) => {
    console.log('Main component handleDisconnect called for appId:', appId);
    try {
      // Find the connection to get app_name and entity_id
      const connection = connectedApps.find(conn => {
        const connAppId = conn.app_id || conn.appId || conn.id;
        return connAppId === appId;
      });

      if (!connection) {
        throw new Error('Connection not found for disconnection');
      }

      // Get app_name from connection or use appId as fallback
      const appName = connection.app_name || connection.name || appId;
      const entityId = connection.entity_id || connection.entityId || null;

      console.log('Calling composioService.disconnectApp with:', { appName, entityId });
      await composioService.disconnectApp(appName, entityId);
      console.log('Disconnect API call successful');

      // Don't refresh here - let the dialog handle the refresh
      // The dialog will call onRefresh after showing success message

      setSnackbar({
        open: true,
        message: `Successfully disconnected from ${selectedApp?.name}`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Main component disconnect error:', err);
      setSnackbar({
        open: true,
        message: `Failed to disconnect from ${selectedApp?.name}`,
        severity: 'error'
      });
      throw err;
    }
  };

  // Stable: the dialog's open/reset effect depends on it, and a new identity on
  // every render would reset the dialog mid-connect.
  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedApp(null);
    // Don't refresh APIs on simple dialog close - only refresh when there are actual changes
  }, []);

  // Create a stable reference for onRefresh to prevent unnecessary re-renders
  const handleRefreshApps = useCallback(() => {
    console.log('handleRefreshApps called from dialog');
    fetchAppsData();
  }, [fetchAppsData]);

  // Re-read connections without the full-page loader, so an open dialog can
  // switch to its Connected view in place after a one-click connect.
  const refreshConnectedApps = useCallback(async () => {
    try {
      const connectedResponse = await composioService.getConnectedApps();
      setConnectedApps(connectedResponse.connections || connectedResponse.connected_apps || []);
    } catch (err) {
      console.error('Error refreshing connected apps:', err);
    }
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Custom Tools handlers
  const handleCustomToolClick = (tool) => {
    if (tool.__shared && tool.__sharePermission !== 'write') {
      setSharedToolPreview(tool);
      return;
    }
    // If it's a user defined tool, open the Edit Definition dialog (CreateCustomToolDialog)
    if (tool.isUserDefined) {
      setEditToolDefinition(tool);
      setCreateToolOpen(true);
      return;
    }

    // Otherwise, check if tool is already configured (for system tools like Gmail)
    const configuredTool = configuredCustomTools.find(
      ct => (ct.tool_name || ct.toolName) === (tool.id || tool.name)
    );

    if (configuredTool) {
      // Edit existing configuration
      setSelectedCustomTool(tool);
      setEditCustomToolConfig(configuredTool);
    } else {
      // Configure new tool
      setSelectedCustomTool(tool);
      setEditCustomToolConfig(null);
    }
    setCustomToolDialogOpen(true);
  };

  const handleCloseCustomToolDialog = () => {
    setCustomToolDialogOpen(false);
    setSelectedCustomTool(null);
    setEditCustomToolConfig(null);
  };

  const handleOpenDeleteDialog = (toolConfig) => {
    setToolToDelete(toolConfig);
    setDeleteType('config');
    setDeleteDialogOpen(true);
  };

  const handleOpenDeleteToolDialog = (e, tool) => {
    e.stopPropagation();
    setToolToDelete(tool);
    setDeleteType('definition');
    setDeleteDialogOpen(true);
  };

  const handleEditToolDefinition = (e, tool) => {
    e.stopPropagation();
    handleCloseToolMenu();
    setEditToolDefinition(tool);
    setCreateToolOpen(true);
  };

  const handleOpenToolMenu = (e, tool, configuredTool, configured) => {
    e.stopPropagation();
    setToolMenuAnchorEl(e.currentTarget);
    setActiveToolForMenu({ tool, configuredTool, configured });
  };

  const handleCloseToolMenu = (e) => {
    if (e && e.stopPropagation) {
      e.stopPropagation();
    }
    setToolMenuAnchorEl(null);
    setActiveToolForMenu(null);
  };

  const handleCloseDeleteDialog = () => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setToolToDelete(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!toolToDelete) return;

    setIsDeleting(true);
    setIsDeleting(true);
    try {
      if (deleteType === 'definition') {
        await customToolsService.deleteTool(toolToDelete.id);
        setSnackbar({
          open: true,
          message: 'Custom tool definition deleted successfully',
          severity: 'success'
        });
      } else {
        await customToolsService.deleteToolConfig(toolToDelete.id);
        setSnackbar({
          open: true,
          message: 'Custom tool configuration deleted successfully',
          severity: 'success'
        });
      }
      setDeleteDialogOpen(false);
      setToolToDelete(null);
      fetchAppsData();
    } catch (err) {
      console.error('Error deleting:', err);
      setSnackbar({
        open: true,
        message: `Failed to delete custom tool ${deleteType}`,
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const isCustomToolConfigured = (toolId) => {
    return configuredCustomTools.some(
      ct => (ct.tool_name || ct.toolName) === toolId
    );
  };

  // Check if an app is connected
  const isAppConnected = (appId) => {
    return connectedApps.some(conn => {
      // Handle both old and new response formats
      const connAppId = conn.app_id || conn.appId || conn.id;
      const connStatus = conn.status || conn.connection_status;
      return connAppId === appId && (connStatus === 'active' || connStatus === 'connected');
    });
  };

  // Get connection ID for connected app
  const getConnectionId = (appId) => {
    const connection = connectedApps.find(conn => {
      const connAppId = conn.app_id || conn.appId || conn.id;
      return connAppId === appId;
    });
    return connection?.connection_id || connection?.id;
  };

  // The shares API keys a composio_app by connected_apps.id (integer PK), which
  // /composio/connected does not return — its `id` is the Composio connection
  // id (string), and sending that 422s with "resource_id: Input should be a
  // valid integer". Until the backend adds `integration_id` there, map
  // app_id → PK from the caller's own rows in /shares/company-resources: the
  // pair (resource.app_id, owner.id) is unique.
  const loadShareIds = useCallback(async () => {
    const me = await rbacService.getMe();
    const ids = new Map();
    const limit = 200;
    for (let offset = 0; ; offset += limit) {
      const result = await resourceSharingService.getCompanyResources({
        resourceType: 'composio_app', userId: me.id, limit, offset,
      });
      const items = Array.isArray(result?.items) ? result.items : [];
      items.forEach(item => {
        if (
          Number(item.owner?.id) === Number(me.id) &&
          ['active', 'connected'].includes(item.resource?.status)
        ) {
          ids.set(String(item.resource?.app_id), Number(item.resource?.id ?? item.resource_id));
        }
      });
      if (items.length < limit || offset + limit >= (Number(result?.total) || 0)) return ids;
    }
  }, []);

  // Loaded ahead of the click so the share dialogs open without waiting on it.
  const shareIdsRef = useRef(null);
  useEffect(() => {
    if (!canShareResources || connectedApps.length === 0) return;
    const pending = loadShareIds();
    shareIdsRef.current = pending;
    pending.catch(() => {
      if (shareIdsRef.current === pending) shareIdsRef.current = null;
    });
  }, [canShareResources, connectedApps, loadShareIds]);

  const resolveShareResourceId = async (appId) => {
    const connection = connectedApps.find(conn => {
      const connAppId = conn.app_id || conn.appId || conn.id;
      return connAppId === appId;
    });
    if (Number.isInteger(connection?.integration_id)) return connection.integration_id;

    const toolkitId = String(connection?.app_id ?? appId);
    if (!shareIdsRef.current) shareIdsRef.current = loadShareIds();
    const ids = await shareIdsRef.current;
    return ids.get(toolkitId) ?? null;
  };

  const openIntegrationSharing = async (appId, name, setIntegration) => {
    try {
      const id = await resolveShareResourceId(appId);
      if (!Number.isInteger(id)) {
        setSnackbar({
          open: true,
          message: `Sharing is unavailable for ${name}: no active connection was found for your account.`,
          severity: 'error'
        });
        return;
      }
      setIntegration({ id, name });
    } catch (err) {
      shareIdsRef.current = null;
      setSnackbar({ open: true, message: describeApiError(err, `Could not open sharing for ${name}.`), severity: 'error' });
    }
  };

  // Get app name for connected app - available for future use
  // const getAppName = (appId) => {
  //   const connection = connectedApps.find(conn => {
  //     const connAppId = conn.app_id || conn.appId || conn.id;
  //     return connAppId === appId;
  //   });
  //   return connection?.app_name || connection?.name;
  // };

  // Get prioritized auth schemes (composio_managed_auth_schemes first, then auth_schemes)
  const getPrioritizedAuthSchemes = (app) => {
    const composioManaged = app.composio_managed_auth_schemes || [];
    const regularAuth = app.auth_schemes || [];

    // If composio_managed_auth_schemes is not empty, use it; otherwise use auth_schemes
    return composioManaged.length > 0 ? composioManaged : regularAuth;
  };

  // Active connections (filtered to connected/active status)
  const activeConnections = useMemo(() => {
    return connectedApps.filter(conn => {
      const status = conn.status || conn.connection_status;
      return status === 'active' || status === 'connected';
    });
  }, [connectedApps]);

  // Apps to render based on the selected filter
  const displayedApps = useMemo(() => {
    if (appsFilter === 'all') return apps;

    const query = (debouncedSearchQuery || '').toLowerCase();
    return activeConnections
      .map(conn => {
        const connAppId = conn.app_id || conn.appId || conn.id;
        const matched = apps.find(a => (a.appId || a.id || a.key) === connAppId);
        if (matched) return matched;
        const fallbackName = conn.app_name || conn.name || connAppId;
        return {
          appId: connAppId,
          id: connAppId,
          key: conn.toolkit_slug || connAppId,
          name: fallbackName,
          description: conn.description || 'Connected app',
          logo: conn.logo || conn.app_logo || conn.toolkit_logo,
          auth_schemes: [],
          actions_count: 0,
          triggers_count: 0,
        };
      })
      .filter(a => {
        if (!query) return true;
        return (
          (a.name || '').toLowerCase().includes(query) ||
          (a.key || '').toLowerCase().includes(query)
        );
      });
  }, [appsFilter, apps, activeConnections, debouncedSearchQuery]);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(1); // Reset to first page when changing page size
  };


  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    // Debounce search to avoid too many API calls
    setPage(1); // Reset to first page when searching
  };

  // Show loading state
  if (loading) {
    return (
      <ThemeProvider theme={muiTheme}>
        <Box className={styles.toolsConfigure}>
          <PageLoader />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={styles.toolsConfigure}>
        {/* Header Section */}
        <PageHeader
          className={styles.toolsPageHeader}
          title="Tools"
          subtitle="Configure and manage external tools and integrations for your agents."
          actionButton={
            <div className={styles.headerActions}>
              <div className={styles.viewSwitch} role="group" aria-label="View mode">
                <Tooltip title="Card view" arrow>
                  <button
                    type="button"
                    className={`${styles.viewSwitchBtn} ${viewMode === 'card' ? styles.viewSwitchBtnActive : ''}`}
                    onClick={() => handleViewModeChange('card')}
                    aria-pressed={viewMode === 'card'}
                    aria-label="Card view"
                  >
                    <LayoutGrid size={18} strokeWidth={2} />
                  </button>
                </Tooltip>
                <Tooltip title="List view" arrow>
                  <button
                    type="button"
                    className={`${styles.viewSwitchBtn} ${viewMode === 'list' ? styles.viewSwitchBtnActive : ''}`}
                    onClick={() => handleViewModeChange('list')}
                    aria-pressed={viewMode === 'list'}
                    aria-label="List view"
                  >
                    <ListIconLucide size={18} strokeWidth={2} />
                  </button>
                </Tooltip>
              </div>
            </div>
          }
        />

        <Box className={styles.content}>
          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Controls — search + refresh on one row (aligned start, like reference) */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 1.5,
              flexWrap: 'nowrap',
              width: '100%',
              minWidth: 0,
            }}
          >
            <TextField
              placeholder="Search apps..."
              value={searchQuery}
              onChange={handleSearchChange}
              size="small"
              fullWidth={false}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: '1 1 auto',
                minWidth: 0,
                maxWidth: { sm: 'min(480px, 50%)' },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: 'background.paper',
                  fontSize: '0.875rem',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(227, 98, 80, 0.35)',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                  borderWidth: 1,
                  boxShadow: '0 0 0 3px rgba(227, 98, 80, 0.12)',
                },
              }}
            />

            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{
                flexShrink: 0,
                alignSelf: 'center',
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '12px',
                px: 2,
                py: 1,
                whiteSpace: 'nowrap',
                borderColor: 'rgba(227, 98, 80, 0.45)',
                bgcolor: 'background.paper',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(227, 98, 80, 0.06)',
                },
              }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>

          {/* Custom Tools Section */}
          <Box sx={{ mb: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 0.5, letterSpacing: '-0.02em', fontSize: '1.25rem' }}>
                  Custom Tools
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontSize: '0.7rem',
                  }}
                >
                  {customTools.length} available • {configuredCustomTools.length} configured
                </Typography>
              </Box>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Add />}
                onClick={() => setCreateToolOpen(true)}
                size="small"
                sx={{
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  fontSize: '0.7rem',
                  borderRadius: '12px',
                  borderWidth: 2,
                  borderColor: 'rgba(227, 98, 80, 0.45)',
                  py: 1,
                  px: 2,
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(227, 98, 80, 0.06)',
                  },
                }}
              >
                Create Custom Tool
              </Button>
            </Box>

            {customTools.length === 0 ? (
              <Alert severity="info">
                No custom tools available at the moment.
              </Alert>
            ) : (
              <>
                {/* Card View */}
                {viewMode === 'card' && (
                  <Box className={styles.toolsContainer}>
                    {customTools.map((tool) => {
                      const toolId = tool.id || tool.name;
                      const configured = isCustomToolConfigured(toolId);
                      const configuredTool = configured ? configuredCustomTools.find(
                        ct => (ct.tool_name || ct.toolName) === toolId
                      ) : null;

                      return (
                        <Card
                          key={toolId}
                          className={styles.toolCard}
                          onClick={() => handleCustomToolClick(tool)}
                          sx={{
                            cursor: 'pointer',
                            position: 'relative',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 4,
                            },
                          }}
                        >
                          {/* Dropdown Menu for Tool Actions */}
                          {(tool.isUserDefined || configured) && (
                            <IconButton
                              onClick={(e) => handleOpenToolMenu(e, tool, configuredTool, configured)}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 1,
                                bgcolor: 'transparent',
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                              size="small"
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          )}

                          <CardContent sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            '&:last-child': { pb: 2 }
                          }}>
                            {/* Header section with icon and name/status */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  bgcolor: 'action.hover',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  fontSize: '1.5rem'
                                }}
                              >
                                {tool.icon || '🔧'}
                              </Box>
                              <Box sx={{ flexGrow: 1, minWidth: 0, pr: configured ? 4 : 0 }}>
                                <Typography
                                  variant="h6"
                                  component="h3"
                                  sx={{
                                    fontWeight: 600,
                                    mb: 0.5,
                                    fontSize: '1rem',
                                    lineHeight: 1.2,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {tool.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    letterSpacing: 0.5,
                                    display: 'block',
                                    mb: 0.5
                                  }}
                                >
                                  {tool.category || 'custom'}
                                </Typography>
                                {(() => {
                                  const isOAuthAuth = tool.auth_type === 'OAUTH2_AUTHORIZATION';
                                  const oauthConnected = Boolean(tool.auth_config?.connected_at);
                                  const badgeLabel = tool.__shared
                                    ? `Shared by ${tool.__shareOwner?.name || tool.__shareOwner?.email || 'teammate'}`
                                    : isOAuthAuth
                                      ? (oauthConnected ? 'Connected' : 'Not Connected')
                                      : (configured ? 'Configured' : 'Available');
                                  const badgeSuccess = isOAuthAuth ? oauthConnected : configured;
                                  return (
                                    <Chip
                                      icon={tool.__shared && tool.__sharePermission === 'read' ? <LockOutlined /> : undefined}
                                      label={badgeLabel}
                                      size="small"
                                      color={badgeSuccess ? 'success' : 'default'}
                                      sx={{
                                        textTransform: 'uppercase',
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        height: 20
                                      }}
                                    />
                                  );
                                })()}
                              </Box>
                            </Box>

                            {/* Description section */}
                            <Box sx={{ flexGrow: 1, mb: 1.5 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  lineHeight: 1.3,
                                  fontSize: '0.875rem',
                                }}
                              >
                                {tool.description}
                              </Typography>
                            </Box>

                            {/* Actions count */}
                            {tool.actions && tool.actions.length > 0 && (
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 'auto' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Build sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    {tool.actions.length} {tool.actions.length === 1 ? 'action' : 'actions'}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}

                {/* List View */}
                {viewMode === 'list' && (
                  <Box className={styles.toolsListContainer}>
                    {customTools.map((tool) => {
                      const toolId = tool.id || tool.name;
                      const configured = isCustomToolConfigured(toolId);
                      const configuredTool = configured ? configuredCustomTools.find(
                        ct => (ct.tool_name || ct.toolName) === toolId
                      ) : null;

                      return (
                        <Box
                          key={toolId}
                          className={styles.toolListItem}
                          onClick={() => handleCustomToolClick(tool)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              borderColor: '#e36250',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            }
                          }}
                        >
                          {/* Tool Icon */}
                          <Avatar className={styles.listItemAvatar}>
                            {tool.icon || '🔧'}
                          </Avatar>

                          {/* Tool Info */}
                          <Box className={styles.listItemInfo}>
                            <Typography variant="subtitle1" className={styles.listItemName}>
                              {tool.name}
                            </Typography>
                            <Typography variant="body2" className={styles.listItemDescription}>
                              {tool.description || 'No description provided'}
                            </Typography>
                          </Box>

                          {/* Stats */}
                          <Box className={styles.listItemStats}>
                            {tool.actions && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Build sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                  {tool.actions.length}
                                </Typography>
                              </Box>
                            )}
                            {(() => {
                              const isOAuthAuth = tool.auth_type === 'OAUTH2_AUTHORIZATION';
                              const oauthConnected = Boolean(tool.auth_config?.connected_at);
                              const badgeLabel = tool.__shared
                                ? `Shared by ${tool.__shareOwner?.name || tool.__shareOwner?.email || 'teammate'}`
                                : isOAuthAuth
                                  ? (oauthConnected ? 'Connected' : 'Not Connected')
                                  : (configured ? 'Configured' : 'Available');
                              const badgeSuccess = isOAuthAuth ? oauthConnected : configured;
                              return (
                                <Chip
                                  icon={tool.__shared && tool.__sharePermission === 'read' ? <LockOutlined /> : undefined}
                                  label={badgeLabel}
                                  size="small"
                                  color={badgeSuccess ? 'success' : 'default'}
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              );
                            })()}
                          </Box>

                          {/* Actions */}
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {tool.isUserDefined && (!tool.__shared || tool.__sharePermission === 'write') && (
                              <Tooltip title="Edit Definition">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleEditToolDefinition(e, tool)}
                                  sx={{ color: 'primary.main' }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {tool.isUserDefined && !tool.__shared && (
                              <Tooltip title="Delete Tool">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleOpenDeleteToolDialog(e, tool)}
                                  sx={{ color: 'error.main' }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {configured && configuredTool && (
                              <Tooltip title="Delete Configuration">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDeleteDialog(configuredTool);
                                  }}
                                  sx={{ color: 'error.main' }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>
            )}

            {/* Render a single shared Menu component for Tools */}
            <Menu
              anchorEl={toolMenuAnchorEl}
              open={Boolean(toolMenuAnchorEl)}
              onClose={handleCloseToolMenu}
              MenuListProps={{
                'aria-labelledby': 'tool-actions-button',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {activeToolForMenu?.tool?.isUserDefined && (!activeToolForMenu?.tool?.__shared || activeToolForMenu?.tool?.__sharePermission === 'write') && (
                <MenuItem onClick={(e) => {
                  handleEditToolDefinition(e, activeToolForMenu.tool);
                }}>
                  <ListItemIcon>
                    <Edit fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Edit Definition</ListItemText>
                </MenuItem>
              )}

              {activeToolForMenu?.configured && activeToolForMenu?.configuredTool && (
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                  handleCloseToolMenu();
                  handleOpenDeleteDialog(activeToolForMenu.configuredTool);
                }}>
                  <ListItemIcon>
                    <Delete fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: 'error.main' }}>Delete Configuration</ListItemText>
                </MenuItem>
              )}

              {!activeToolForMenu?.tool?.__shared && canShareResources && (
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                  setToolToShare(activeToolForMenu.tool);
                  handleCloseToolMenu();
                }}>
                  <ListItemIcon><Share fontSize="small" /></ListItemIcon>
                  <ListItemText>Share</ListItemText>
                </MenuItem>
              )}

              {!activeToolForMenu?.tool?.__shared && canShareResources && (
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                  setToolToManage(activeToolForMenu.tool);
                  handleCloseToolMenu();
                }}>
                  <ListItemIcon><ManageAccounts fontSize="small" /></ListItemIcon>
                  <ListItemText>Manage sharing</ListItemText>
                </MenuItem>
              )}

              {activeToolForMenu?.tool?.isUserDefined && !activeToolForMenu?.tool?.__shared && (
                <MenuItem onClick={(e) => {
                  e.stopPropagation();
                  handleCloseToolMenu();
                  handleOpenDeleteToolDialog(e, activeToolForMenu.tool);
                }}>
                  <ListItemIcon>
                    <Delete fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText sx={{ color: 'error.main' }}>Delete Tool</ListItemText>
                </MenuItem>
              )}
            </Menu>
          </Box>

          {/* Apps Section */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 700, mb: 0.5, letterSpacing: '-0.02em', fontSize: '1.25rem' }}>
                  Apps
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    fontSize: '0.7rem',
                  }}
                >
                  {appsFilter === 'connected'
                    ? (searchQuery
                        ? `Showing ${displayedApps.length} of ${activeConnections.length} connected`
                        : `${activeConnections.length} Connected`)
                    : `${searchQuery
                        ? `Showing ${totalApps} of ${totalAllApps} apps`
                        : `${totalAllApps} total apps`} • ${activeConnections.length} Connected`}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box
                  className={styles.viewSwitch}
                  role="group"
                  aria-label="Apps filter"
                  sx={{ display: 'inline-flex', gap: 0.5 }}
                >
                  <Tooltip title="Show all apps" arrow>
                    <button
                      type="button"
                      className={`${styles.viewSwitchBtn} ${appsFilter === 'all' ? styles.viewSwitchBtnActive : ''}`}
                      onClick={() => handleAppsFilterChange('all')}
                      aria-pressed={appsFilter === 'all'}
                      style={{
                        width: 'auto',
                        minWidth: 'unset',
                        padding: '0 12px',
                        height: 32,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      All
                    </button>
                  </Tooltip>
                  <Tooltip title="Show only connected apps" arrow>
                    <button
                      type="button"
                      className={`${styles.viewSwitchBtn} ${appsFilter === 'connected' ? styles.viewSwitchBtnActive : ''}`}
                      onClick={() => handleAppsFilterChange('connected')}
                      aria-pressed={appsFilter === 'connected'}
                      style={{
                        width: 'auto',
                        minWidth: 'unset',
                        padding: '0 12px',
                        height: 32,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Connected ({activeConnections.length})
                    </button>
                  </Tooltip>
                </Box>

                {appsFilter === 'all' && (
                  <>
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.12em',
                        fontSize: '0.62rem',
                        display: { xs: 'none', sm: 'inline' },
                      }}
                    >
                      Per Page
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        aria-label="Apps per page"
                        sx={{
                          borderRadius: '12px',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'divider',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(227, 98, 80, 0.35)',
                          },
                        }}
                      >
                        <MenuItem value={20}>20</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                      </Select>
                    </FormControl>
                  </>
                )}
              </Box>
            </Box>

            {displayedApps.length === 0 ? (
              <Alert severity="info">
                {appsFilter === 'connected'
                  ? (searchQuery
                      ? 'No connected apps match your search.'
                      : 'No connected apps yet. Connect an app from the All tab to see it here.')
                  : (searchQuery
                      ? `No apps found matching your search`
                      : 'No apps available. Please check your connection.')}
              </Alert>
            ) : (
              <>
                {/* Card View */}
                {viewMode === 'card' && (
                  <Box className={styles.toolsContainer}>
                    {displayedApps.map((app) => {
                      const appId = app.appId || app.id || app.key;
                      const connected = isAppConnected(appId);
                      return (
                        <Card
                          key={appId}
                          className={styles.toolCard}
                          onClick={() => handleAppClick(app)}
                          sx={{
                            cursor: 'pointer',
                            position: 'relative',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 4,
                            },
                          }}
                        >
                          {connected && canShareResources && (
                            <Box sx={{ position: 'absolute', top: 6, right: 6, zIndex: 2, display: 'flex' }}>
                              <Tooltip title="Share integration"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openIntegrationSharing(appId, app.name, setIntegrationToShare); }}><Share fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Manage sharing"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openIntegrationSharing(appId, app.name, setIntegrationToManage); }}><ManageAccounts fontSize="small" /></IconButton></Tooltip>
                            </Box>
                          )}
                          <CardContent sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            '&:last-child': { pb: 2 }
                          }}>
                            {/* Header section with icon and name/status */}
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  bgcolor: 'action.hover',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  overflow: 'hidden',
                                }}
                              >
                                {app.logo ? (
                                  <Box
                                    component="img"
                                    src={app.logo}
                                    alt={app.name}
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'contain',
                                    }}
                                  />
                                ) : (
                                  <OpenInNew sx={{ color: 'primary.main' }} />
                                )}
                              </Box>
                              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <Typography
                                  variant="h6"
                                  component="h3"
                                  sx={{
                                    fontWeight: 600,
                                    mb: 0.5,
                                    fontSize: '1rem',
                                    lineHeight: 1.2,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {app.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    textTransform: 'uppercase',
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    letterSpacing: 0.5,
                                    display: 'block',
                                    mb: 0.5
                                  }}
                                >
                                  {app.key}
                                </Typography>
                                <Chip
                                  label={connected ? 'Connected' : 'Available'}
                                  size="small"
                                  color={connected ? 'success' : 'default'}
                                  sx={{
                                    textTransform: 'uppercase',
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    height: 20
                                  }}
                                />
                              </Box>
                            </Box>

                            {/* Description section */}
                            <Box sx={{ flexGrow: 1, mb: 1.5 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  lineHeight: 1.3,
                                  fontSize: '0.875rem',
                                  mb: 1
                                }}
                              >
                                {app.description}
                              </Typography>

                              {/* Actions and Triggers count */}
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Build sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    {app.actions_count || 0}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Bolt sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    {app.triggers_count || 0}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>

                            {/* Auth schemes section - always at bottom */}
                            <Box sx={{ mt: 'auto' }}>
                              <Stack
                                direction="row"
                                spacing={0.5}
                                flexWrap="wrap"
                                sx={{
                                  gap: 0.5,
                                  alignItems: 'flex-start'
                                }}
                              >
                                {getPrioritizedAuthSchemes(app)?.map((scheme, index) => {
                                  let chipColor = 'default';
                                  let chipVariant = 'outlined';

                                  // Color coding for different auth schemes
                                  switch (scheme) {
                                    case 'OAUTH2':
                                      chipColor = 'primary';
                                      chipVariant = 'filled';
                                      break;
                                    case 'API_KEY':
                                      chipColor = 'secondary';
                                      chipVariant = 'filled';
                                      break;
                                    case 'BEARER_TOKEN':
                                      chipColor = 'info';
                                      chipVariant = 'filled';
                                      break;
                                    case 'SERVICE_ACCOUNT':
                                      chipColor = 'default';
                                      chipVariant = 'outlined';
                                      break;
                                    case 'NO_AUTH':
                                      chipColor = 'success';
                                      chipVariant = 'outlined';
                                      break;
                                    default:
                                      chipColor = 'default';
                                      chipVariant = 'outlined';
                                  }

                                  return (
                                    <Chip
                                      key={index}
                                      label={scheme}
                                      size="small"
                                      color={chipColor}
                                      variant={chipVariant}
                                      sx={{
                                        fontSize: '0.65rem',
                                        height: 20,
                                        fontWeight: 600,
                                        '& .MuiChip-label': {
                                          px: 0.75
                                        }
                                      }}
                                    />
                                  );
                                })}
                              </Stack>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}

                {/* List View */}
                {viewMode === 'list' && (
                  <Box className={styles.toolsListContainer}>
                    {displayedApps.map((app) => {
                      const appId = app.appId || app.id || app.key;
                      const connected = isAppConnected(appId);

                      return (
                        <Box
                          key={appId}
                          className={styles.toolListItem}
                          onClick={() => handleAppClick(app)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              borderColor: '#e36250',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            }
                          }}
                        >
                          {/* App Logo */}
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: 2,
                              bgcolor: 'action.hover',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              overflow: 'hidden',
                            }}
                          >
                            {app.logo ? (
                              <Box
                                component="img"
                                src={app.logo}
                                alt={app.name}
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain',
                                }}
                              />
                            ) : (
                              <OpenInNew sx={{ color: 'primary.main' }} />
                            )}
                          </Box>

                          {/* App Info */}
                          <Box className={styles.listItemInfo}>
                            <Typography variant="subtitle1" className={styles.listItemName}>
                              {app.name}
                            </Typography>
                            <Typography variant="body2" className={styles.listItemDescription}>
                              {app.description || 'No description provided'}
                            </Typography>
                          </Box>

                          {/* Auth Schemes */}
                          <Box className={styles.listItemConfig}>
                            <Stack direction="row" spacing={1}>
                              {getPrioritizedAuthSchemes(app)?.slice(0, 2).map((scheme, index) => (
                                <Chip
                                  key={index}
                                  label={scheme}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 20 }}
                                />
                              ))}
                              {getPrioritizedAuthSchemes(app)?.length > 2 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{getPrioritizedAuthSchemes(app).length - 2}
                                </Typography>
                              )}
                            </Stack>
                          </Box>

                          {/* Status */}
                          <Chip
                            label={connected ? 'Connected' : 'Available'}
                            size="small"
                            color={connected ? 'success' : 'default'}
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                          {connected && canShareResources && (
                            <Box sx={{ display: 'flex' }}>
                              <Tooltip title="Share integration"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openIntegrationSharing(appId, app.name, setIntegrationToShare); }}><Share fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Manage sharing"><IconButton size="small" onClick={(e) => { e.stopPropagation(); openIntegrationSharing(appId, app.name, setIntegrationToManage); }}><ManageAccounts fontSize="small" /></IconButton></Tooltip>
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>
            )}

            {/* Pagination Controls */}
            {appsFilter === 'all' && apps.length > 0 && totalPages > 1 && (
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 4,
                flexWrap: 'wrap',
                gap: 2
              }}>
                <Typography variant="body2" color="text.secondary">
                  Page {page} of {totalPages} • Showing {apps.length} apps
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<NavigateBefore />}
                    onClick={() => handlePageChange(page - 1)}
                    disabled={!hasPrevious}
                    sx={{ textTransform: 'none' }}
                  >
                    Previous
                  </Button>

                  {/* Page Numbers */}
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNum}
                          variant={page === pageNum ? 'contained' : 'outlined'}
                          size="small"
                          onClick={() => handlePageChange(pageNum)}
                          sx={{
                            minWidth: 40,
                            textTransform: 'none',
                            ...(page === pageNum && {
                              background: 'linear-gradient(135deg, #e36250, #f87171)',
                            })
                          }}
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </Box>

                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<NavigateNext />}
                    onClick={() => handlePageChange(page + 1)}
                    disabled={!hasNext}
                    sx={{ textTransform: 'none' }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Connection Dialog */}
        <ComposioConnectionDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          app={selectedApp}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
          isConnected={selectedApp ? isAppConnected(selectedApp.appId || selectedApp.id) : false}
          connectionId={selectedApp ? getConnectionId(selectedApp.appId || selectedApp.id) : null}
          onRefresh={handleRefreshApps}
          onConnected={refreshConnectedApps}
        />

        {/* Custom Tool Config Dialog */}
        <CustomToolConfigDialog
          open={customToolDialogOpen}
          onClose={handleCloseCustomToolDialog}
          tool={selectedCustomTool}
          editConfig={editCustomToolConfig}
          onRefresh={fetchAppsData}
        />

        <CreateCustomToolDialog
          open={createToolOpen}
          onClose={() => {
            setCreateToolOpen(false);
            setEditToolDefinition(null);
          }}
          onRefresh={fetchAppsData}
          editTool={editToolDefinition}
        />

        {/* Custom Tool Delete Confirmation Dialog */}
        <CustomToolDeleteDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleConfirmDelete}
          toolConfig={toolToDelete}
          isDeleting={isDeleting}
          isDefinition={deleteType === 'definition'}
        />

        <ResourceShareDialog
          open={Boolean(toolToShare)}
          resource={toolToShare ? { type: 'custom_tool', id: toolToShare.id, name: toolToShare.name } : null}
          onClose={() => setToolToShare(null)}
          onSuccess={(result) => {
            const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
            setSnackbar({ open: true, message: warnings.length ? `Tool shared. ${warnings.join(' ')}` : 'Custom tool shared successfully.', severity: warnings.length ? 'warning' : 'success' });
          }}
        />
        <ManageSharingDialog
          open={Boolean(toolToManage)}
          resource={toolToManage ? { type: 'custom_tool', id: toolToManage.id, name: toolToManage.name } : null}
          onClose={() => setToolToManage(null)}
          onChanged={fetchAppsData}
        />

        <Dialog open={Boolean(sharedToolPreview)} onClose={() => setSharedToolPreview(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Shared custom tool</DialogTitle>
          <DialogContent dividers>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{sharedToolPreview?.name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Shared by {sharedToolPreview?.__shareOwner?.name || sharedToolPreview?.__shareOwner?.email || 'a teammate'} · Read only
            </Typography>
            <Box sx={{ display: 'grid', gap: 1, mt: 2 }}>
              <Typography variant="body2"><strong>Tool key:</strong> {sharedToolPreview?.tool_key || '—'}</Typography>
              <Typography variant="body2"><strong>API type:</strong> {sharedToolPreview?.api_type || '—'}</Typography>
              <Typography variant="body2" color="text.secondary">
                You can use this tool in your own agents from the Add Tool dialog. Editing its definition remains with the owner.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions><Button onClick={() => setSharedToolPreview(null)}>Close</Button></DialogActions>
        </Dialog>
        <ResourceShareDialog
          open={Boolean(integrationToShare)}
          resource={integrationToShare ? { type: 'composio_app', id: integrationToShare.id, name: integrationToShare.name } : null}
          onClose={() => setIntegrationToShare(null)}
          onSuccess={(result) => {
            const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
            setSnackbar({ open: true, message: warnings.length ? `Integration shared. ${warnings.join(' ')}` : 'Integration shared successfully.', severity: warnings.length ? 'warning' : 'success' });
          }}
        />
        <ManageSharingDialog
          open={Boolean(integrationToManage)}
          resource={integrationToManage ? { type: 'composio_app', id: integrationToManage.id, name: integrationToManage.name } : null}
          onClose={() => setIntegrationToManage(null)}
          onChanged={fetchAppsData}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default ToolsConfigure;
